import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { inviteTraveler } from '@/lib/api';
import { Mail, UserPlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function InviteTravelerModal({ tripId, open, onClose, onSuccess }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('visitor');
  const [loading, setLoading] = useState(false);

  const handleInvite = async () => {
    if (!email.trim()) {
      toast.error('Email requis');
      return;
    }

    setLoading(true);
    try {
      await inviteTraveler(tripId, email, name, role);
      toast.success(`Invitation envoyée à ${email}`);
      
      // Reset form
      setEmail('');
      setName('');
      setRole('visitor');
      
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de l\'invitation');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-purple-500" />
            Inviter un participant
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="exemple@email.com"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="name">Nom (optionnel)</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Prénom Nom"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="role">Rôle</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="organizer">
                  <div className="flex flex-col items-start">
                    <span className="font-medium">Organisateur</span>
                    <span className="text-xs text-gray-500">Accès complet + gestion</span>
                  </div>
                </SelectItem>
                <SelectItem value="visitor">
                  <div className="flex flex-col items-start">
                    <span className="font-medium">Visiteur</span>
                    <span className="text-xs text-gray-500">Consultation + participation</span>
                  </div>
                </SelectItem>
                <SelectItem value="kids">
                  <div className="flex flex-col items-start">
                    <span className="font-medium">Enfant (&lt; 12 ans)</span>
                    <span className="text-xs text-gray-500">Interface simplifiée + gamification</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              {role === 'organizer' && '✅ Peut tout gérer et inviter d\'autres personnes'}
              {role === 'visitor' && '👥 Peut voir l\'itinéraire et participer aux activités'}
              {role === 'kids' && '🎮 Interface gamifiée pour les enfants de moins de 12 ans'}
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex gap-2">
              <Mail className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-800">
                <p className="font-medium mb-1">Un email d'invitation sera envoyé</p>
                <p>Le participant recevra un lien pour rejoindre le voyage avec le rôle sélectionné.</p>
              </div>
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Annuler
            </Button>
            <Button
              onClick={handleInvite}
              disabled={loading || !email.trim()}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Envoyer l'invitation
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
